import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '@/lib/api'
import { getUserTimezone } from '@/lib/timezone'
import { useAuth } from '@/hooks/useAuth'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import { useToast } from '@/hooks/use-toast'
import { useImpersonation } from '@/hooks/useImpersonation'
import MetricCard from '@/components/analytics/MetricCard'
import ChatVolumeChart from '@/components/analytics/ChatVolumeChart'
import ActivityFeed from '@/components/analytics/ActivityFeed'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DashboardHero, type TickerItem } from '@/components/dashboard-ui'
import {
  LiveStatusCard,
  MiniConversionFunnel,
  QuickInsights,
  AnalyticsTabs
} from '@/components/dashboard'
import {
  MessageSquare,
  Clock,
  Phone,
  PhoneMissed,
  PhoneCall,
  Calendar,
  Sparkles,
  ArrowRight
} from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface DashboardMetrics {
  activeChats: number
  waitingQueue: number
  onlineAgents: number
  todayChats: number
  avgResponseTime: number
  completionRate: number
  bookingsToday: number
  todayCalls: number
  callsAnswered: number
  callsMissed: number
  callAnswerRate: number
  avgCallDuration: number
  totalCallMinutes: number
  followUpsNeeded: number
}

interface MetricChanges {
  todayChats: number | null
  avgResponseTime: number | null
  completionRate: number | null
  todayCalls: number | null
}

interface Sparklines {
  todayChats: number[]
  completionRate: number[]
}

interface FunnelStage {
  name: string
  count: number
  color: string
}

interface PeakHour {
  hour: number
  count: number
}

interface TopAgent {
  name: string
  chats: number
}

export default function DashboardPage() {
  const { agent } = useAuth()
  const featureGate = useFeatureGate()
  // Associate tier has no voice — the Voice section on the dashboard becomes
  // meaningless (all zeros, no action). Soft-blur it with a subtle Upgrade
  // chip instead of removing it entirely, so users understand it exists on
  // higher tiers.
  const voiceMuted = !featureGate.isAllowed('ai_inbound_agent')
  const { toast } = useToast()
  const { isImpersonating } = useImpersonation()
  const isAdmin = agent?.role === 'admin' || agent?.role === 'supervisor' || isImpersonating

  const [timeframe, setTimeframe] = useState('today')
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    activeChats: 0,
    waitingQueue: 0,
    onlineAgents: 1,
    todayChats: 0,
    avgResponseTime: 0,
    completionRate: 0,
    bookingsToday: 0,
    todayCalls: 0,
    callsAnswered: 0,
    callsMissed: 0,
    callAnswerRate: 0,
    avgCallDuration: 0,
    totalCallMinutes: 0,
    followUpsNeeded: 0
  })
  const [changes, setChanges] = useState<MetricChanges>({
    todayChats: null,
    avgResponseTime: null,
    completionRate: null,
    todayCalls: null
  })
  const [sparklines, setSparklines] = useState<Sparklines>({
    todayChats: [],
    completionRate: []
  })
  const [chartData, setChartData] = useState<Array<{ date: string; count: number }>>([])
  const [activities, setActivities] = useState<any[]>([])
  const [funnelData, setFunnelData] = useState<FunnelStage[]>([])
  const [peakHour, setPeakHour] = useState<PeakHour | undefined>()
  const [topAgent, setTopAgent] = useState<TopAgent | undefined>()
  // Today strip — appointments for today/tomorrow OR fallback insights
  // when no upcoming appointments. Drives the kinetic ticker.
  const [todayStrip, setTodayStrip] = useState<TickerItem[]>([])
  const [loading, setLoading] = useState(true)
  const [voiceCalls, setVoiceCalls] = useState(0)
  // null while loading — avoids a flash of the empty-state banner for users
  // who actually have agents.
  const [assistantCount, setAssistantCount] = useState<number | null>(null)

  useEffect(() => {
    loadDashboardData()
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000)
    return () => clearInterval(interval)
  }, [timeframe])

  useEffect(() => {
    api.get('/api/voice/assistants')
      .then((res) => {
        const list = Array.isArray(res.data?.assistants) ? res.data.assistants : []
        setAssistantCount(list.length)
      })
      .catch(() => {
        // On transient failure keep it null — don't flash a "set up your first agent"
        // banner to a returning user who actually has agents.
      })
  }, [])

  const loadDashboardData = async () => {
    try {
      // Each endpoint is individually catchable so a single transient
      // failure (or one slow endpoint) doesn't blow up the whole load and
      // surface "Dashboard load failed" while 5/6 panels actually have
      // fresh data. console.error names the specific endpoint so we can
      // see in browser devtools which one is the actual culprit.
      const fail = (name: string) => (err: any) => {
        // eslint-disable-next-line no-console
        console.error(`[Dashboard load] ${name} failed:`, err?.response?.status, err?.message || err)
        return { data: { success: false } as any }
      }
      const [
        dashboardResponse,
        analyticsResponse,
        activityResponse,
        funnelResponse,
        agentsResponse,
        todayStripResponse
      ] = await Promise.all([
        api.get(`/api/analytics/dashboard?timezone=${encodeURIComponent(getUserTimezone())}&timeframe=${timeframe}`).catch(fail('dashboard')),
        api.get(`/api/analytics/overview?timeframe=${timeframe}`).catch(fail('overview')),
        api.get(`/api/analytics/recent-activity?limit=10&timeframe=${timeframe}`).catch(fail('recent-activity')),
        api.get(`/api/analytics/funnel?timeframe=${timeframe}`).catch(fail('funnel')),
        api.get(`/api/analytics/agents?timeframe=${timeframe}`).catch(fail('agents')),
        api.get(`/api/analytics/today-strip?timezone=${encodeURIComponent(getUserTimezone())}`).catch(fail('today-strip'))
      ])

      if (dashboardResponse.data.success) {
        const { metrics: dashboardMetrics } = dashboardResponse.data
        setMetrics({
          activeChats: dashboardMetrics.activeChats || 0,
          waitingQueue: dashboardMetrics.waitingQueue || 0,
          onlineAgents: dashboardMetrics.onlineAgents || 0,
          todayChats: dashboardMetrics.todayChats || 0,
          avgResponseTime: dashboardMetrics.avgResponseTime || 0,
          completionRate: dashboardMetrics.completionRate || 0,
          bookingsToday: dashboardMetrics.bookingsToday || 0,
          todayCalls: dashboardMetrics.todayCalls || 0,
          callsAnswered: dashboardMetrics.callsAnswered || 0,
          callsMissed: dashboardMetrics.callsMissed || 0,
          callAnswerRate: dashboardMetrics.callAnswerRate || 0,
          avgCallDuration: dashboardMetrics.avgCallDuration || 0,
          totalCallMinutes: dashboardMetrics.totalCallMinutes || 0,
          followUpsNeeded: dashboardMetrics.followUpsNeeded || 0
        })
        setChanges(dashboardMetrics.changes || {
          todayChats: null,
          avgResponseTime: null,
          completionRate: null,
          todayCalls: null,
        })
        setSparklines(dashboardMetrics.sparklines || {
          todayChats: [],
          completionRate: []
        })
        setVoiceCalls(dashboardMetrics.activeVoiceCalls || 0)
        if (dashboardMetrics.peakHour) {
          setPeakHour(dashboardMetrics.peakHour)
        }
      }

      if (analyticsResponse.data.success) {
        setChartData(analyticsResponse.data.analytics.chartData || [])
      }

      if (activityResponse.data.success) {
        setActivities(activityResponse.data.activities || [])
      }

      if (funnelResponse.data.success) {
        setFunnelData(funnelResponse.data.funnelData || [])
      }

      if (todayStripResponse.data?.success && Array.isArray(todayStripResponse.data.items)) {
        setTodayStrip(todayStripResponse.data.items.map((it: any) => ({
          label: it.label,
          meta: it.meta,
          tone: it.tone,
          href: it.href,
        })))
      }

      if (agentsResponse.data.success && agentsResponse.data.agentPerformance?.length > 0) {
        const topPerformer = agentsResponse.data.agentPerformance[0]
        // Only show top performer if they actually handled chats in this org
        if (topPerformer.totalConversations > 0) {
          setTopAgent({
            name: topPerformer.name,
            chats: topPerformer.totalConversations
          })
        } else {
          setTopAgent(undefined)
        }
      }

      // Only surface the toast when EVERY endpoint failed — partial
      // failures already show up as zeroed cards / empty lists, and
      // toasting on every minute-refresh because one endpoint blipped
      // trains the user to ignore real warnings.
      const anySucceeded =
        dashboardResponse.data?.success ||
        analyticsResponse.data?.success ||
        activityResponse.data?.success ||
        funnelResponse.data?.success ||
        agentsResponse.data?.success ||
        todayStripResponse.data?.success
      if (!anySucceeded) {
        toast({ title: 'Dashboard load failed', description: 'Some data may be unavailable. Try refreshing the page.', variant: 'destructive' })
      }
      setLoading(false)
    } catch (error) {
      // Reaches here only on a synchronous throw after Promise.all (e.g.
      // a property-access bug). Log loudly so it's diagnosable in devtools.
      // eslint-disable-next-line no-console
      console.error('[Dashboard load] unexpected post-fetch failure:', error)
      toast({ title: 'Dashboard load failed', description: 'Some data may be unavailable. Try refreshing the page.', variant: 'destructive' })
      setLoading(false)
    }
  }

  const periodLabelShort = timeframe === 'today' ? 'Today' : timeframe === 'yesterday' ? 'Yesterday' : timeframe === '7d' ? '7 Days' : timeframe === '30d' ? '30 Days' : '90 Days'

  // Determine trend: green for up, gray for mild down, red for significant drop (>50%)
  const getTrend = (change: number | null, invertGood = false): 'up' | 'down' | 'down-bad' | 'neutral' => {
    if (change === null || change === 0) return 'neutral'
    if (invertGood) {
      // For metrics where lower is better (response time)
      if (change < 0) return 'up'
      if (change > 50) return 'down-bad'
      return 'down'
    }
    if (change > 0) return 'up'
    if (change < -50) return 'down-bad'
    return 'down'
  }

  // Today strip — backend returns either today/tomorrow's appointments
  // (with action context) or fallback insights when nothing is scheduled.
  // Replaces the old "stock ticker" recent-activity scroll.
  const tickerItems: TickerItem[] = todayStrip.length > 0
    ? todayStrip
    : [
        { label: 'AI receptionist online', meta: 'standing by', tone: 'green' },
        { label: 'Listening for inbound calls', tone: 'periwinkle' },
        { label: 'Web widget · ready to greet visitors', tone: 'violet' },
      ]

  return (
    <div className="aurora-mesh space-y-5 sm:space-y-6">
      {/* No-agents empty-state banner. Replaced a full indigo→royal gradient
          fill (reads as "AI Purple slop" per design-taste skill) with a
          neutral card + a single brand-royal accent stripe on the left edge.
          Brand identity appears as an accent, not as wallpaper. */}
      {assistantCount === 0 && (
        <Card className="border-l-[3px] border-l-brand-royal">
          <CardContent className="py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-royal/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-brand-royal" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base tracking-tight">Set up your first AI agent</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Your dashboard comes alive once your AI agents start taking calls and chats. Setup is a few minutes.
                </p>
                <div className="mt-4">
                  <Button
                    asChild
                    size="sm"
                    className="bg-brand-royal hover:bg-brand-royal/90 text-white active:scale-[0.98] [transition:transform_140ms_cubic-bezier(0.23,1,0.32,1),background-color_200ms_ease-out]"
                  >
                    <Link to="/agents">
                      Go to AI Agents
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <DashboardHero
        eyebrow={(() => {
          const period =
            timeframe === 'today' ? 'Today'
            : timeframe === 'yesterday' ? 'Yesterday'
            : timeframe === '7d' ? 'Last 7 days'
            : timeframe === '30d' ? 'Last 30 days'
            : timeframe === '90d' ? 'Last 90 days'
            : timeframe.toUpperCase()
          return agent?.name ? `${agent.name.split(' ')[0]} · ${period}` : period
        })()}
        headlineValue={metrics.todayCalls + metrics.todayChats}
        headlineLabel={(() => {
          const total = metrics.todayCalls + metrics.todayChats
          const period =
            timeframe === 'today' ? 'today'
            : timeframe === 'yesterday' ? 'yesterday'
            : timeframe === '7d' ? 'in the last 7 days'
            : timeframe === '30d' ? 'in the last 30 days'
            : timeframe === '90d' ? 'in the last 90 days'
            : 'in this window'
          if (total === 0) {
            return 'Your AI is online and waiting. The first conversation lands here in real time.'
          }
          // The supporting pills below carry secondary metrics — keep the
          // headline label to one strong sentence. (Was: " — 36 booked,
          // 100% answer rate" tail duplicating the pills.)
          return `conversations handled by your AI ${period}.`
        })()}
        supporting={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand-periwinkle" /> {metrics.activeChats} active chat{metrics.activeChats === 1 ? '' : 's'}</span>
            <span className="text-muted-foreground/40">·</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {metrics.callsAnswered}/{metrics.todayCalls} calls answered</span>
            <span className="text-muted-foreground/40">·</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {metrics.totalCallMinutes} min on calls</span>
          </div>
        }
        actions={
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        }
        ticker={tickerItems}
        status={assistantCount && assistantCount > 0 ? 'live' : 'quiet'}
        liveActivity={metrics.activeChats + voiceCalls}
      />

      {/* Chat Performance — bento layout: the LIVE tile is twice as wide as
          the period KPIs. Asymmetry kills the "4-up admin template" feel
          and reinforces that "what's happening now" is the priority signal. */}
      <section>
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Chat — {periodLabelShort}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
          <div className="lg:col-span-2">
            <LiveStatusCard
              title="Active Chats"
              value={metrics.activeChats}
              icon={MessageSquare}
              status="normal"
              pulse={metrics.activeChats > 0}
              subtitle={metrics.activeChats > 0 ? 'In progress' : 'None right now'}
              loading={loading}
              href="/chats"
            />
          </div>
          <MetricCard
            title="Chats"
            value={metrics.todayChats}
            change={changes.todayChats}
            changeLabel="vs yesterday"
            trend={getTrend(changes.todayChats)}
            icon={MessageSquare}
            iconColor="text-muted-foreground"
            sparklineData={sparklines.todayChats}
            loading={loading}
            href={`/conversations?filter=${timeframe === 'yesterday' ? 'yesterday' : timeframe}`}
          />
          <MetricCard
            title="Avg Response"
            value={`${metrics.avgResponseTime}s`}
            change={changes.avgResponseTime !== null ? Math.abs(changes.avgResponseTime) : null}
            changeLabel="vs yesterday"
            trend={getTrend(changes.avgResponseTime, true)}
            icon={Clock}
            iconColor="text-muted-foreground"
            loading={loading}
            href="/analytics"
          />
          <MetricCard
            title="Bookings"
            value={metrics.bookingsToday || 0}
            icon={Calendar}
            iconColor={metrics.bookingsToday > 0 ? 'text-emerald-500' : 'text-muted-foreground'}
            loading={loading}
            href="/bookings"
          />
        </div>
      </section>

      {/* Voice Performance — bento with live tile spanning 2 cols on lg.
          When the tier doesn't include voice, the four-card grid is replaced
          by ONE upsell card. Selling beats apologising — opacity:0.6 made
          the section look broken instead of locked. */}
      <section aria-label="Voice performance">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Voice — {periodLabelShort}
          </h2>
        </div>
        {voiceMuted ? (
          <Card className="relative overflow-hidden border-l-[3px] border-l-brand-royal">
            {/* Subtle gradient wash on the right — adds depth without "AI slop" */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-1/2"
              style={{
                background: 'radial-gradient(ellipse at right center, hsl(var(--grad-royal) / 0.08) 0%, transparent 70%)',
              }}
            />
            <CardContent className="relative py-6 sm:py-7">
              <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
                <div className="h-12 w-12 rounded-xl bg-brand-royal/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-brand-royal" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base tracking-tight">Answer every call. Book on the phone.</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Your AI receptionist takes inbound calls 24/7, qualifies leads, and books consults straight to your calendar.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    asChild
                    size="sm"
                    className="bg-brand-royal hover:bg-brand-royal/90 text-white active:scale-[0.98] [transition:transform_140ms_cubic-bezier(0.23,1,0.32,1),background-color_200ms_ease-out]"
                  >
                    <Link to="/settings/billing">
                      Unlock Voice
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
            <div className="lg:col-span-2">
              <LiveStatusCard
                title="Live Calls"
                value={voiceCalls}
                icon={Phone}
                status={voiceCalls > 0 ? 'voice' : 'normal'}
                pulse={voiceCalls > 0}
                subtitle={voiceCalls > 0 ? 'Active now' : 'No active calls'}
                loading={loading}
                href="/calls"
              />
            </div>
            <MetricCard
              title="Calls"
              value={metrics.todayCalls}
              change={changes.todayCalls}
              changeLabel="vs yesterday"
              trend={getTrend(changes.todayCalls)}
              icon={Phone}
              iconColor="text-muted-foreground"
              loading={loading}
              href={`/calls?date=${timeframe === 'yesterday' ? 'yesterday' : timeframe}`}
            />
            <MetricCard
              title="Answer Rate"
              value={`${metrics.callAnswerRate}%`}
              icon={PhoneCall}
              iconColor={metrics.callAnswerRate >= 80 ? 'text-emerald-500' : metrics.callAnswerRate > 0 ? 'text-amber-500' : 'text-muted-foreground'}
              loading={loading}
              href="/calls"
            />
            <MetricCard
              title="Pending Follow-ups"
              value={metrics.followUpsNeeded}
              icon={PhoneMissed}
              iconColor={metrics.followUpsNeeded > 0 ? 'text-amber-500' : 'text-muted-foreground'}
              loading={loading}
              href="/follow-up-queue"
            />
          </div>
        )}
      </section>

      {/* Primary Visualizations - 2 Column */}
      <section>
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Performance Trends
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChatVolumeChart data={chartData} loading={loading} />
          <MiniConversionFunnel data={funnelData} loading={loading} />
        </div>
      </section>

      {/* Activity & Insights - 2 Column */}
      <section>
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Activity & Insights
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityFeed activities={activities} loading={loading} />
          <QuickInsights
            peakHour={peakHour}
            topAgent={topAgent}
            avgWaitTime={metrics.avgResponseTime}
            queueWarning={metrics.waitingQueue > 2}
            bookingsToday={metrics.bookingsToday}
            loading={loading}
            timeframe={timeframe as any}
          />
        </div>
      </section>

      {/* Secondary Analytics - Tabbed — follows the global timeframe so the
          numbers don't contradict the cards above when the user picks 30d/90d. */}
      {isAdmin && (
        <section>
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {(() => {
              const period =
                timeframe === 'today' ? 'Today'
                : timeframe === 'yesterday' ? 'Yesterday'
                : timeframe === '7d' ? 'Last 7 Days'
                : timeframe === '30d' ? 'Last 30 Days'
                : timeframe === '90d' ? 'Last 90 Days'
                : 'Selected Range'
              return `Detailed Analytics — ${period}`
            })()}
          </h2>
          <AnalyticsTabs timeframe={timeframe as any} />
        </section>
      )}
    </div>
  )
}
